import { authFetch } from './http/fetchClient';
import { APIUrl } from 'Utils/Utils';
import { ChatResponseFile } from '../../components/Chat/types';

const BASE_URL = `${APIUrl()}/history`;

const EXTRA_HEADERS = {
  'access-control-allow-origin': '*',
  'content-type': 'application/json',
};

// ── Types ────────────────────────────────────────────────────────────────────

export interface ChatSession {
  session_id: string;
  chat_name: string;
  updated_at: string;
  chat_folder_id: number | null;
}

export interface ChatFolder {
  id: number;
  chat_folder_name: string;
  updated_at: string;
}

// One exchange from GET /fetch_chat_history — same `files` shape as the
// live WebSocket response format (ChatResponseFile), just nested under a
// query/response pair instead of a single ai_response. Covers both
// previewable documents and generated images (each entry's file_type is
// what tells Chat.tsx/MessageList which is which) — a generated-image row's
// chat_response is just placeholder text like "Here is your image
// (image_id=...)"; MessageList suppresses it and shows the image instead,
// same as it does for the equivalent live-WS case.
export interface ChatHistoryRow {
  turn_id: string | null;
  chat_query: string;
  chat_response: string;
  files: ChatResponseFile[];
  created_at: string;
}

// ── Checking list responses ──────────────────────────────────────────────────
// The sidebar is on screen all the time and draws these lists directly, so a
// malformed response used to blank the whole app. They are checked here,
// where the JSON enters the app: a response that isn't a list counts as a
// failed request (callers already handle that), and entries that can't be
// shown are dropped, keeping the rest. Well-formed entries come back as is.

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isUsableId = (value: unknown): boolean =>
  (typeof value === 'string' && value !== '') || typeof value === 'number';

const listFrom = (data: unknown, what: string): unknown[] => {
  if (!Array.isArray(data)) throw new Error(`Malformed ${what} response`);
  return data;
};

const keepShowable = <T>(entries: unknown[], toEntry: (raw: unknown) => T | null, what: string): T[] => {
  const kept = entries.map(toEntry).filter((entry): entry is T => entry !== null);
  if (kept.length < entries.length) {
    console.warn(`[chat-history] skipped ${entries.length - kept.length} malformed ${what}`);
  }
  return kept;
};

// A name that isn't text (e.g. an object) can't be drawn, so it becomes ''.
const toChatSession = (raw: unknown): ChatSession | null => {
  if (!isRecord(raw) || !isUsableId(raw.session_id)) return null;
  return (typeof raw.chat_name === 'string' ? raw : { ...raw, chat_name: '' }) as unknown as ChatSession;
};

const toChatFolder = (raw: unknown): ChatFolder | null => {
  if (!isRecord(raw) || !isUsableId(raw.id)) return null;
  return (typeof raw.chat_folder_name === 'string' ? raw : { ...raw, chat_folder_name: '' }) as unknown as ChatFolder;
};

const toChatSessions = (data: unknown, what: string): ChatSession[] =>
  keepShowable(listFrom(data, what), toChatSession, what);

// ── API calls ────────────────────────────────────────────────────────────────

export async function fetchChatSessions(userEmail: string): Promise<ChatSession[]> {
  const res = await authFetch(`${BASE_URL}/fetch_chat_sessions`, { headers: EXTRA_HEADERS });
  if (!res.ok) throw new Error('Failed to fetch chat sessions');
  return toChatSessions(await res.json(), 'chat sessions');
}

export async function fetchFolders(userEmail: string): Promise<ChatFolder[]> {
  const res = await authFetch(`${BASE_URL}/list_folders`, { headers: EXTRA_HEADERS });
  if (!res.ok) throw new Error('Failed to fetch folders');
  return keepShowable(listFrom(await res.json(), 'folders'), toChatFolder, 'folders');
}

export async function fetchFolderSessions(
  userEmail: string,
  folderId: number
): Promise<ChatSession[]> {
  const res = await authFetch(`${BASE_URL}/fetch_folder_sessions?chat_folder_id=${folderId}`, {
    headers: EXTRA_HEADERS,
  });
  if (!res.ok) throw new Error('Failed to fetch folder sessions');
  return toChatSessions(await res.json(), 'folder sessions');
}

// `signal` lets the caller cancel the request (e.g. the user left this chat).
export async function fetchChatHistory(
  userEmail: string,
  sessionId: string,
  page = 1,
  signal?: AbortSignal
): Promise<ChatHistoryRow[]> {
  const res = await authFetch(`${BASE_URL}/fetch_chat_history?session_id=${sessionId}&page=${page}`, {
    headers: EXTRA_HEADERS,
    signal,
  });
  if (!res.ok) throw new Error('Failed to fetch chat history');
  return res.json();
}

export interface GenerateChatTitleResponse {
  session_id: string;
  chat_name: string;
}

// Explicitly triggers backend title generation for a session — the
// frontend now decides *when* to ask for a name (see useWebSocket.tsx and
// Chat.tsx's callers) instead of the backend naming sessions on its own
// after some fixed turn count. Safe to call more than once for the same
// session: the backend only fills in a name if one isn't already set, it
// never overwrites an existing one. Returns null when there's nothing to
// name yet (404: no session or turns) rather than throwing, since callers
// treat that as a no-op.
export async function generateChatTitle(sessionId: string): Promise<GenerateChatTitleResponse | null> {
  const res = await authFetch(`${BASE_URL}/generate_chat_title/${sessionId}`, {
    method: 'POST',
    headers: EXTRA_HEADERS,
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error('Failed to generate chat title');
  return res.json();
}

export async function renameSession(
  sessionId: string,
  userEmail: string,
  newChatName: string
): Promise<boolean> {
  const res = await authFetch(`${BASE_URL}/rename_chat_session/${sessionId}`, {
    method: 'PATCH',
    headers: EXTRA_HEADERS,
    body: JSON.stringify({ new_chat_name: newChatName }),
  });

  if (!res.ok) return false;
  const json = await res.json();
  return json.success;
}

export async function deleteSession(sessionId: string, userEmail: string): Promise<boolean> {
  const res = await authFetch(`${BASE_URL}/delete_chat_session/${sessionId}`, {
    method: 'DELETE',
    headers: EXTRA_HEADERS,
  });
  if (!res.ok) return false;

  const json = await res.json();
  return json.success;
}

export async function createFolder(
  userEmail: string,
  chatFolderName: string
): Promise<ChatFolder | false> {
  const res = await authFetch(`${BASE_URL}/create_folder`, {
    method: 'POST',
    headers: EXTRA_HEADERS,
    body: JSON.stringify({ chat_folder_name: chatFolderName }),
  });

  if (!res.ok) return false;
  return res.json();
}

export async function renameFolder(
  folderId: number,
  userEmail: string,
  newName: string
): Promise<boolean> {
  const res = await authFetch(`${BASE_URL}/folders/${folderId}/rename`, {
    method: 'PATCH',
    headers: EXTRA_HEADERS,
    body: JSON.stringify({ new_chat_folder_name: newName }),
  });

  if (!res.ok) return false;
  const json = await res.json();
  return json.success;
}

export async function deleteFolder(folderId: number, userEmail: string): Promise<boolean> {
  const res = await authFetch(`${BASE_URL}/folders/${folderId}`, {
    method: 'DELETE',
    headers: EXTRA_HEADERS,
  });
  if (!res.ok) return false;

  const json = await res.json();
  return json.success;
}

export async function moveSession(
  sessionId: string,
  userEmail: string,
  newFolderId: number | null
): Promise<boolean> {
  const res = await authFetch(`${BASE_URL}/move_chat_session/${sessionId}`, {
    method: 'PATCH',
    headers: EXTRA_HEADERS,
    body: JSON.stringify({ new_chat_folder_id: newFolderId }),
  });

  if (!res.ok) return false;
  const json = await res.json();
  return json.success;
}
